import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Pagination,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import { db } from "../../firebase";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";

function Productsize() {
  const [sizes, setSizes] = useState([]);
  const [filteredSizes, setFilteredSizes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [sizeValue, setSizeValue] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const collectionRef = collection(db, "product_sizes");

  // Fetch data from Firebase
  const fetchData = async () => {
    const snap = await getDocs(collectionRef);
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setSizes(data);
    setFilteredSizes(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Search filter
  useEffect(() => {
    const filtered = sizes.filter((item) =>
      item.size.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredSizes(filtered);
    setPage(1);
  }, [search, sizes]);

  // Open add/edit dialog
  const handleOpenDialog = (mode = false, id = null, value = "") => {
    setEditMode(mode);
    setCurrentId(id);
    setSizeValue(value);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSizeValue("");
    setCurrentId(null);
  };

  // Add new size
  const handleAdd = async () => {
    if (!sizeValue) return;
    await addDoc(collectionRef, { size: sizeValue });
    fetchData();
    handleCloseDialog();
  };

  // Update size
  const handleUpdate = async () => {
    if (!sizeValue || !currentId) return;
    const docRef = doc(db, "product_sizes", currentId);
    await updateDoc(docRef, { size: sizeValue });
    fetchData();
    handleCloseDialog();
  };

  // Delete size
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this size?")) {
      await deleteDoc(doc(db, "product_sizes", id));
      fetchData();
    }
  };

  // Pagination
  const paginatedData = filteredSizes.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Product Sizes
      </Typography>

      {/* Search + Add */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          placeholder="Search Size"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={() => handleOpenDialog(false)}>
          Add Size
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ mb: 2 }} >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                <TableCell>{item.size}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(true, item.id, item.size)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No Sizes Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Pagination
        count={Math.ceil(filteredSizes.length / rowsPerPage)}
        page={page}
        onChange={(e, value) => setPage(value)}
        color="primary"
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editMode ? "Edit Size" : "Add Size"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Size"
            value={sizeValue}
            onChange={(e) => setSizeValue(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editMode ? handleUpdate : handleAdd}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Productsize;